import { EventTypes, HISTORY_ACTION_MODE, nodesFlux, useEditorSelector } from '@candulabs/craft';
import { css, styled, TrashIcon } from 'canoe';
import React, { useMemo, useState } from 'react';

import { ValueBox, ValueStepper, ValueToggle } from '@candu-ai/craft/ui';
import { useNodesData } from 'craft/hooks';
import { useColumnOperations } from 'craft/hooks/useColumnOperations';
import { useEditor } from 'craft/hooks/useEditor';

interface Props {
  nodeId: string;
  index: number;
  selected: boolean;
  total: number;
}

const StyledValueBox = styled(ValueBox)<{ selected: boolean; isHovering: boolean }>`
  ${({ theme, selected, isHovering }) => css`
    cursor: pointer;

    background-color: ${theme.colors.White};

    ${isHovering &&
    css`
      background-color: ${theme.colors.GlitterGrayL30};
    `}

    ${selected &&
    css`
      background-color: ${theme.colors.GlitterGrayL20};
    `}
  `}
`;

const SizeControls = ({ nodeId }: { nodeId: string }) => {
  const nodeData = useNodesData([nodeId])[0];
  const { increaseColumn, decreaseColumn } = useColumnOperations({
    parentNodeId: nodeData.parent,
  });
  const customSelector = useMemo(() => nodesFlux.selectors.getNodeCustom(nodeId), [nodeId]);
  const custom = useEditorSelector(customSelector);
  if (!custom.custom) {
    // No custom most likely means node is in the process of being deleted
    return <></>;
  }
  const { width } = custom?.custom;

  return (
    <ValueStepper
      value={width}
      editable={false}
      minValue={1}
      maxValue={11}
      valueSuffix="/12"
      onChange={(newValue: number) => {
        if (newValue > width) {
          increaseColumn?.(nodeId);
        } else {
          decreaseColumn?.(nodeId);
        }
      }}
    />
  );
};

export const ColumnValueRow = ({ nodeId, index, selected }: Props) => {
  const { actions, query } = useEditor();
  const [isHovering, setIsHovering] = useState(false);

  // Columns should only contain 'Column' children but it might be possible with Copy + Paste to break this
  if (query.node(nodeId).node?.data?.name !== 'Column') {
    return null;
  }

  const onDeleteColumn = () => {
    actions.deleteNodes(nodeId);
  };

  const onMouseEnter = () => {
    setIsHovering(true);
    actions.setEventNodes(EventTypes.hovered, [nodeId], HISTORY_ACTION_MODE.IGNORE);
  };

  const onClick = () => {
    actions.setEventNodes(EventTypes.selected, [nodeId], HISTORY_ACTION_MODE.IGNORE);
  };

  const onMouseLeave = () => {
    setIsHovering(false);
    actions.setEventNodes(EventTypes.hovered, [], HISTORY_ACTION_MODE.IGNORE);
  };

  return (
    <StyledValueBox
      label={`Column ${index + 1}`}
      selected={selected}
      isHovering={isHovering}
      onClick={onClick}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      isInGroup={index === 0 ? { isFirst: true } : true}
      allowChildrenGrow={false}
    >
      <SizeControls nodeId={nodeId} />
      <ValueToggle
        key={`column-delete-${index}`}
        toggleGroup="column-delete"
        id={`column-delete-${index}`}
        onClick={onDeleteColumn}
        isActive={false}
        label={<TrashIcon color="OnyxGrayL20" size="s" />}
      />
    </StyledValueBox>
  );
};
